import * as APIRoutes from '@mm/api-routes';
import { AudiencesPaginationModel } from '../model/AudiencePaginationModel';
import AudiencesModel from '../model/AudiencesModel';
import { VariableOptionsModel } from '../model/VariableOptionsModel';
import AuthenticatedController from './AuthenticatedController';

export default class AudiencesController extends AuthenticatedController {
  static async getAll(
    botId: number,
    pageNumber = 1,
    pageSize = 25,
    query?: string,
  ): Promise<AudiencesPaginationModel> {
    return super.get(APIRoutes.AUDIENCES(botId, pageNumber, pageSize, query));
  }

  static async getAudienceById(
    botId: number,
    audienceId: number,
  ): Promise<AudiencesModel> {
    return super.get(`${APIRoutes.BOTS}/${botId}/audiences/${audienceId}`);
  }

  static async getAllCSV(botId: number, audienceId: number) {
    return super.get(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/contacts_csv`,
    );
  }

  static async createScheduledExport(
    botId: number,
    audienceId: number,
    emailAddress?: string,
  ) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/scheduled_exports`,
      { emailAddress },
    );
  }

  static async getContactsById(
    botId: number,
    audienceId: number,
    facebookPageId: number,
    pageNumber = 1,
    pageSize = 999999,
    query = '',
  ) {
    const routeQuery = APIRoutes.AUDIENCE_CONTACTS(
      botId,
      audienceId,
      pageNumber,
      pageSize,
      facebookPageId,
      query,
    );
    return super.get(routeQuery);
  }

  static async postAudience(
    entity: Partial<AudiencesModel>,
    botId: number,
  ): Promise<AudiencesModel> {
    const response = await super.post(
      `${APIRoutes.BOTS}/${botId}/audiences`,
      entity,
    );
    return response.audience;
  }

  static async putAudience(
    updatedAudience: Partial<AudiencesModel>,
    botId: number,
  ) {
    const { name, filterParams, tagsFilters, deleteContacts } = updatedAudience;
    return super.put(
      `${APIRoutes.BOTS}/${botId}/audiences/${updatedAudience.id}`,
      { name, filterParams, tagsFilters, deleteContacts },
    );
  }

  static async deleteContacts(
    botId: number,
    audienceId: number,
    deleteContacts: boolean,
  ) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/delete_contacts`,
      { deleteContacts },
    );
  }

  static async getVariables(botId: number) {
    const response = await super.get(
      `${APIRoutes.BOTS}/${botId}/audiences/variables`,
    );
    return response.variables;
  }

  static async getVariableOptions(
    botId: number,
    variableId: number,
    variableType: string,
    variableName: string,
  ) {
    const url = `${APIRoutes.BOTS}/${botId}/audiences/variable_options`;
    const options = `variable_id=${variableId}&variable_type=${variableType}&variable_name=${variableName}`;

    return (await super.get(`${url}?${options}`)) as VariableOptionsModel;
  }

  static async deleteAudience(audienceId: number, botId: number) {
    return super.delete(`${APIRoutes.BOTS}/${botId}/audiences/${audienceId}`);
  }

  static async cloneAudience(audienceId: number, botId: number) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/clone`,
    );
  }
}
